/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase/client/supabaseClient';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';

interface ShopAccount {
  id: string;
  shop_name: string;
  owner_name: string;
  contact_number: string;
  email: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gst_number: string;
  kyc_status: string;
  aadhaar: string;
  pan_card_path: string;
  bank_proof_path: string;
  passport_photo_path: string;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  branch_name: string;
  created_at: string;
  updated_at: string;
  shop_id: string | null;
  secret: string;
  lat: number;
  lon: number;
}

interface DocumentViewerProps {
  documentPath: string;
  isOpen: boolean;
  closeModal: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ documentPath, isOpen, closeModal }) => {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Determine bucket based on the document type
  const getBucketAndPath = (docPath: string) => {
    if (docPath.includes('-aadhaar')) {
      return { bucket: 'aadhar', path: docPath };
    } else if (docPath.includes('-passport_photo')) {
      return { bucket: 'passport-photos', path: docPath };
    } else if (docPath.includes('-bank_proof')) {
      return { bucket: 'bank-proof', path: docPath };
    } else if (docPath.includes('-pan_card')) {
      return { bucket: 'pan', path: docPath };
    }
    return { bucket: '', path: docPath }; // Fallback
  };
  
  const fileExtension = documentPath?.split('.').pop()?.toLowerCase();

  useEffect(() => {
    if (isOpen && documentPath) {
      const fetchDocument = async () => {
        try {
          setLoading(true);
          const { bucket, path } = getBucketAndPath(documentPath);
          
          if (!bucket) {
            throw new Error('Could not determine document bucket');
          }
          
          const { data, error } = await supabase.storage
            .from(bucket)
            .createSignedUrl(path, 60); // URL expires in 60 seconds

          if (error) throw error;
          setDocumentUrl(data.signedUrl);
        } catch (err) {
          setError('Failed to load document');
          console.error('Error loading document:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchDocument();
    }
  }, [isOpen, documentPath]);

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={closeModal}>
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black opacity-30" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
          
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Document Preview
              </Dialog.Title>
              
              {loading ? (
                <div className="flex justify-center items-center h-96">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="flex justify-center items-center h-96 text-red-500">{error}</div>
              ) : (
                <div className="mt-2">
                  {fileExtension === 'pdf' ? (
                    <iframe 
                      src={`${documentUrl}#toolbar=0`} 
                      className="w-full h-[70vh]" 
                      title="PDF document"
                    />
                  ) : (
                    <div className="flex justify-center">
                      <img 
                        src={documentUrl || ''} 
                        alt="Document preview" 
                        className="max-h-[70vh] max-w-full object-contain"
                      />
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

// Add isEmbedded prop to component props
interface KYCVerificationPageProps {
  isEmbedded?: boolean;
}

const KYCVerificationPage: React.FC<KYCVerificationPageProps> = ({ isEmbedded = false }) => {
  const [shops, setShops] = useState<ShopAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('submitted');
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    fetchShops();
  }, [filter]);

  const fetchShops = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('shop_accounts')
        .select('*')
        .eq('kyc_status', filter);

      if (error) throw error;
      setShops(data || []);
    } catch (err) {
      console.error('Error fetching shops:', err);
      toast.error('Failed to load shop data');
    } finally {
      setLoading(false);
    }
  };

  const openDocumentModal = (documentPath: string) => {
    if (!documentPath) {
      toast.error('No document available for preview');
      return;
    }
    setSelectedDocument(documentPath);
    setIsModalOpen(true);
  };

  const approveShop = async (shop: ShopAccount) => {
    setProcessingId(shop.id);
    try {
      // 1. Create a new entry in print_shops table
      const { data, error } = await supabase.schema('api').rpc('create_and_link_print_shop', {
  p_shop_name: shop.shop_name,
  p_address: shop.address,
  p_city: shop.city,
  p_state: shop.state,
  p_contact_number: shop.contact_number,
  p_lat: shop.lat,
  p_lon: shop.lon,
  p_secret: shop.secret,
  p_pincode: shop.pincode,
  p_shop_account_id: shop.id
});

if (error) throw error;
console.log('Created shop with ID:', data.shop_id);

      toast.success('Shop approved successfully');
      fetchShops(); // Refresh the list
    } catch (err) {
      console.error('Error approving shop:', err);
      toast.error('Failed to approve shop');
    } finally {
      setProcessingId(null);
    }
  };

  const rejectShop = async (shopId: string) => {
    setProcessingId(shopId);
    try {
      const { error } = await supabase
        .from('shop_accounts')
        .update({ kyc_status: 'rejected' })
        .eq('id', shopId);

      if (error) throw error;
      
      toast.success('Shop application rejected');
      fetchShops(); // Refresh the list
    } catch (err) {
      console.error('Error rejecting shop:', err);
      toast.error('Failed to reject shop');
    } finally {
      setProcessingId(null);
    }
  };

  // Render content only (for embedding in tabs)
  const renderContent = () => (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-medium text-gray-900">Shop Applications</h2>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {shops.length} {filter.replace('_', ' ')}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Filter:</span>
          <select
            className="form-select text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="submitted">Under Review</option>
            <option value="waiting for document upload">Waiting for Documents</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : shops.length === 0 ? (
        <div className="py-20 text-center text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="mt-2">No applications found with status: {filter.replace('_', ' ')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop Details</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Details</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shops.map((shop) => (
                <tr key={shop.id} className="hover:bg-gray-50">
                  {/* Shop details cell */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{shop.shop_name}</div>
                        <div className="text-sm text-gray-500">Owner: {shop.owner_name}</div>
                        <div className="text-sm text-gray-500">Email: {shop.email}</div>
                        <div className="text-sm text-gray-500">Phone: {shop.contact_number}</div>
                      </div>
                    </div>
                  </td>
                  
                  {/* Location cell */}
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{shop.address}</div>
                    <div className="text-sm text-gray-500">{shop.city}, {shop.state} - {shop.pincode}</div>
                    {shop.lat && shop.lon && (
                      <div className="text-xs text-gray-400 mt-1">
                        Lat: {shop.lat.toFixed(6)}, Lon: {shop.lon.toFixed(6)}
                      </div>
                    )}
                  </td>
                  
                  {/* Bank details cell */}
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{shop.account_holder_name || 'N/A'}</div>
                    <div className="text-sm text-gray-500">
                      {shop.account_number ? 
                        `A/C: ${shop.account_number}` : 
                        'No account number'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {shop.bank_name && shop.branch_name ? 
                        `${shop.bank_name}, ${shop.branch_name}` : 
                        shop.bank_name || 'No bank details'}
                    </div>
                    {shop.ifsc_code && (
                      <div className="text-sm text-gray-500">IFSC: {shop.ifsc_code}</div>
                    )}
                  </td>
                  
                  {/* Documents cell */}
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-2">
                      {shop.aadhaar && (
                        <button
                          onClick={() => openDocumentModal(shop.aadhaar)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Aadhaar Card
                        </button>
                      )}
                      {shop.pan_card_path && (
                        <button
                          onClick={() => openDocumentModal(shop.pan_card_path)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          PAN Card
                        </button>
                      )}
                      {shop.bank_proof_path && (
                        <button
                          onClick={() => openDocumentModal(shop.bank_proof_path)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Bank Proof
                        </button>
                      )}
                      {shop.passport_photo_path && (
                        <button
                          onClick={() => openDocumentModal(shop.passport_photo_path)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Passport Photo
                        </button>
                      )}
                    </div>
                  </td>
                  
                  {/* Actions cell */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {filter === 'submitted' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => approveShop(shop)}
                          disabled={processingId === shop.id}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                        >
                          {processingId === shop.id ? 'Processing...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => rejectShop(shop.id)}
                          disabled={processingId === shop.id}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        >
                          {processingId === shop.id ? 'Processing...' : 'Reject'}
                        </button>
                      </div>
                    )}
                    {filter === 'verified' && (
                      <div className="flex items-center">
                        <span className="flex items-center text-green-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Verified
                        </span>
                      </div>
                    )}
                    {filter === 'rejected' && (
                      <div className="flex items-center">
                        <span className="flex items-center text-red-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Rejected
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Document viewer modal */}
      {selectedDocument && (
        <DocumentViewer 
          documentPath={selectedDocument}
          isOpen={isModalOpen}
          closeModal={() => {
            setIsModalOpen(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </>
  );

  // If embedded, just return the content without full page layout
  if (isEmbedded) {
    return renderContent();
  }

  // Otherwise return full page with header and footer
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">KYC Verification</h1>
            <p className="text-sm text-gray-600 mt-1">
              Verify and approve shop registration documents
            </p>
          </div>
          <Link
            to="/admin"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Admin
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            {renderContent()}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default KYCVerificationPage;
